import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { ApiError } from 'src/shared/utils/handleAxiosError';

import { addToast } from 'src/shared/components/ui';

const NETWORK_ERROR_TOAST_KEY = 'network-error';
const NETWORK_ERROR_RETRY_LIMIT = 2;

/**
 * Transient network errors (no HTTP response at all) happen most often when
 * a browser tab has been frozen/backgrounded and resumes onto a dead
 * keep-alive socket. We want to retry those a couple of times with a short
 * backoff so the user never sees the failure — but we should NOT retry on
 * real HTTP errors (4xx/5xx), which are legitimate server responses.
 */
const isNetworkError = (error: unknown): boolean =>
  error instanceof ApiError && error.code === 'ERR_NETWORK';

const shouldRetry = (failureCount: number, error: unknown): boolean =>
  isNetworkError(error) && failureCount < NETWORK_ERROR_RETRY_LIMIT;

const retryDelay = (attemptIndex: number): number =>
  Math.min(500 * 2 ** attemptIndex, 5000);

// De-dupe the toast so a burst of failing queries only shows one message.
const shownToasts = new Set<string>();

const showNetworkErrorToastOnce = (): void => {
  if (shownToasts.has(NETWORK_ERROR_TOAST_KEY)) return;

  addToast({
    title: 'Network error. Please check your connection.',
    color: 'danger',
    timeout: 5000,
    onClose: () => shownToasts.delete(NETWORK_ERROR_TOAST_KEY)
  });

  shownToasts.add(NETWORK_ERROR_TOAST_KEY);
};

const handleGlobalError = (error: unknown): void => {
  if (isNetworkError(error)) {
    showNetworkErrorToastOnce();
  }
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: handleGlobalError
  }),
  mutationCache: new MutationCache({
    onError: handleGlobalError
  }),
  defaultOptions: {
    queries: {
      retry: shouldRetry,
      retryDelay,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 3
    },
    mutations: {
      retry: shouldRetry,
      retryDelay
    }
  }
});

export default queryClient;
